import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Result } from '@app/models/common/result';
import { Constant } from '@app/models/enums/constant.enum';
import { LoginAuthType } from '@app/models/enums/login-auth-type.enum';
import { RescType } from '@app/models/enums/resc-type.enum';
import { FeatureSubsc } from '@app/models/feature-subsc';
import { LoginUser } from '@app/models/login-user';
import { AuthResource } from '@app/models/settings/auth-resource';
import { cleanAppStorage, getItem, setItem, StorageItem, treeToList } from '@core/utils';
import { ROUTER_UTILS } from '@core/utils/router.utils';
import { environment } from '@environments/environment';
import { isEmpty, pick } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  //allows other components to subscribe to the currentUser Observable but doesn't allow them to publish to the currentUserSubject
  private currentUserSubject: BehaviorSubject<LoginUser>;
  public currentUserObservale: Observable<LoginUser>;
  public isLoggedInSubject: BehaviorSubject<boolean>;

  public grpSubscptSubject: BehaviorSubject<FeatureSubsc>;
  public attrCfgSubject: BehaviorSubject<Record<string, string>>;

  public showModulesSubject: BehaviorSubject<AuthResource[]> = new BehaviorSubject<AuthResource[]>([]);
  public navListSubject: BehaviorSubject<AuthResource[]> = new BehaviorSubject<AuthResource[]>([]);

  constructor(private http: HttpClient, private router: Router, private location: Location) {
    this.currentUserSubject = new BehaviorSubject<LoginUser>(JSON.parse(<string>getItem(StorageItem.AuthUser) || '{}'));
    this.currentUserObservale = this.currentUserSubject.asObservable();
    this.isLoggedInSubject = new BehaviorSubject<boolean>(!!getItem(StorageItem.Token));

    this.grpSubscptSubject = new BehaviorSubject<FeatureSubsc>(JSON.parse(<string>getItem(StorageItem.Subsc) || '{}'));
    this.attrCfgSubject = new BehaviorSubject<Record<string, string>>(
      JSON.parse(<string>getItem(StorageItem.AttrCfg) || '{}'),
    );
  }

  public get currentUser(): LoginUser {
    return this.currentUserSubject.value;
  }

  public get isLoggedIn(): boolean {
    return this.isLoggedInSubject.getValue();
  }

  public get hasMecRole(): boolean {
    return !!this.currentUser.authorities?.map(ele => ele.authority)?.length;
  }

  public get isFreeTrialUser() {
    return !!this.currentUser.authorities?.find(ele => ele.authority === Constant.ROLE_FREE_TRIAL);
  }

  public get isPowerOrAdminUser() {
    return !!this.currentUser.authorities?.find(
      ele => ele.authority.startsWith(Constant.CLIENT_POWER) || ele.authority === Constant.SUPER_ADMIN,
    );
  }

  signIn(payload: any): Observable<LoginUser | undefined> {
    const url = `${environment.apiUrl}/user/login`;

    return this.http.post<LoginUser>(url, payload, { observe: 'response' }).pipe(
      map((response: any) => {
        if (!response.body.data) return;

        const authKey = response.body.data['authKey'];
        if (!isEmpty(authKey)) {
          // 需要短信验证码(二步登录验证)
          return response.body.data;
        }
        const user: LoginUser = pick(response.body.data, ['userId', 'username', 'authorities', 'defaultRoute']);
        const token = response.headers.get('token');

        if (!token) return;

        // store user details and jwt token in local storage to keep user logged in between page refreshes
        setItem(StorageItem.AuthUser, JSON.stringify(user));
        setItem(StorageItem.Token, token);
        this.currentUserSubject.next(user);
        this.isLoggedInSubject.next(true);
        return user;
      }),
    );
  }

  // getSubscription(): Observable<FeatureSubsc | null> {
  //   const url = `${environment.apiUrl}/user/subscription`;
  //   return this.http.get<Result<any>>(url).pipe(
  //     map(res => {
  //       const data: FeatureSubsc | null = res.data;
  //       if (data) {
  //         data.startPeriod = toMonth(data.startPeriod);
  //         data.endPeriod = toMonth(data.endPeriod);
  //         data.beginDataPeriod = toMonth(data.beginDataPeriod);
  //         data.latestDataPeriod = toMonth(data.latestDataPeriod);
  //         setItem(StorageItem.Subsc, JSON.stringify(data));
  //         this.grpSubscptSubject.next(data);
  //       }
  //       return data;
  //     }),
  //   );
  // }

  getAuthRescList(): Observable<Array<AuthResource>> {
    const url = `${environment.apiUrl}/resc/granted/tree`;
    return this.http.get<Result<Array<AuthResource>>>(url).pipe(
      map(res => {
        const data: AuthResource[] = res.data;
        if (!isEmpty(data)) {
          const attrCfg: Record<string, string> = treeToList(data)
            .filter(ele => ele.type === RescType.ATTR)
            .reduce((result, item: AuthResource) => {
              result[item.code] = item.value;
              return result;
            }, {});
          setItem(StorageItem.AttrCfg, JSON.stringify(attrCfg));
          this.attrCfgSubject.next(attrCfg);
          this.navListSubject.next(data);
        }
        return data;
      }),
    );
  }

  sendValidCodeMessage(authKey: string, authType: LoginAuthType): Observable<boolean> {
    const url = `${environment.apiUrl}/sender/code`;
    return this.http.post<Result<boolean>>(url, { authKey, authType }).pipe(map(res => res.data));
  }

  sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  silenceSignOut(): Observable<any> {
    const url = `${environment.apiUrl}/logout`;
    return this.http.post<Result<any>>(url, null).pipe(map(res => res.data));
  }

  redirectToLogin() {
    const returnUrl = this.location.path(true);
    const uid = this.currentUser.userId;
    const { root, login } = ROUTER_UTILS.config.auth;
    const queryParams: Record<string, any> = {};
    if (returnUrl && returnUrl !== '/') queryParams['returnUrl'] = returnUrl;
    if (uid != null) queryParams['uid'] = uid;
    const loginUrl = this.location.prepareExternalUrl(
      this.router.serializeUrl(this.router.createUrlTree(['/', root, login],
        { queryParams: Object.keys(queryParams).length ? queryParams : undefined }))
    );
    cleanAppStorage();
    // loginUrl is built from hardcoded route constants (auth/login), safe to assign
    window.location.assign(loginUrl);
  }

  signOut(): void {
    const url = `${environment.apiUrl}/logout`;
    this.http.post(url, null).subscribe(async () => {
      this.redirectToLogin();
      await this.sleep(1000);

      this.currentUserSubject.next({} as LoginUser);
      this.isLoggedInSubject.next(false);
      this.grpSubscptSubject.next({} as FeatureSubsc);
      this.attrCfgSubject.next({} as Record<string, string>);

    });
  }

  sendValidCode(username: string, sendMark: string): Observable<any> {
    const url = `${environment.apiUrl}/system/forgotPwd/sendValidCode`;
    return this.http.post<Result<any>>(url, { username, sendMark }).pipe(map(res => res.data));
  }

  checkValidCode(username: string, validCode: string): Observable<boolean> {
    const url = `${environment.apiUrl}/system/forgotPwd/checkValidCode`;
    return this.http.post<Result<boolean>>(url, { username, validCode }).pipe(map(res => res.data));
  }

  setNewPwd(username: string, newPassword: string): Observable<boolean> {
    const url = `${environment.apiUrl}/system/forgotPwd/setNewPwd`;
    return this.http.post<Result<boolean>>(url, { username, newPassword }).pipe(map(res => res.data));
  }
}
